import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Plus, FolderKanban, MapPin, Calendar } from 'lucide-react';
import axiosInstance from '../lib/axios';
import { toast } from 'sonner';
import { usePermissions } from '../contexts/PermissionContext';
import { useTranslation } from 'react-i18next';

const Projects = () => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    location: '',
    start_date: '',
    end_date: ''
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axiosInstance.get('/projects');
      setProjects(response.data);
    } catch (error) {
      toast.error(t('messages.error.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axiosInstance.post('/projects', formData);
      toast.success(t('messages.success.created'));
      setOpen(false);
      setFormData({ name: '', code: '', location: '', start_date: '', end_date: '' });
      fetchProjects();
    } catch (error) {
      toast.error(t('messages.error.failed'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="projects-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-black tracking-tight">{t('projects.title')}</h1>
          <p className="text-muted-foreground mt-2">{t('projects.allProjects')}</p>
        </div>
        {hasPermission('create_project') && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2" data-testid="create-project-button">
                <Plus className="w-4 h-4" />
                {t('projects.createProject')}
              </Button>
            </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('projects.createProject')}</DialogTitle>
              <DialogDescription>{t('projects.createFirstProject')}</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">{t('projects.projectName')}</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="project-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="code">{t('projects.projectCode')}</Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  required
                  placeholder="e.g., PROJ-001"
                  data-testid="project-code-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="location">{t('projects.location')}</Label>
                <Input
                  id="location"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  required
                  data-testid="project-location-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start_date">{t('projects.startDate')}</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    required
                    data-testid="project-start-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end_date">{t('projects.endDate')}</Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    required
                    data-testid="project-end-date-input"
                  />
                </div>
              </div>
              <Button type="submit" className="w-full" data-testid="submit-project-button">
                {t('projects.createProject')}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {projects.length === 0 ? (
        <Card className="p-12">
          <div className="text-center">
            <FolderKanban className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-xl font-bold mb-2">{t('projects.noProjectsYet')}</h3>
            <p className="text-muted-foreground mb-4">
              {hasPermission('create_project') 
                ? t('projects.createFirstProject')
                : t('projects.noProjectsAssigned')
              }
            </p>
            {hasPermission('create_project') && (
              <Button onClick={() => setOpen(true)} data-testid="create-first-project-button">
                <Plus className="w-4 h-4 mr-2" />
                {t('projects.createProject')}
              </Button>
            )}
          </div>
        </Card>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {projects.map((project) => (
            <Link key={project.id} to={`/projects/${project.id}`} data-testid={`project-card-${project.id}`}>
              <Card className="h-full hover:border-primary transition-colors">
                <CardHeader>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <CardTitle className="truncate">{project.name}</CardTitle>
                      <CardDescription className="truncate">
                        <code className="text-xs font-mono">{project.code}</code>
                      </CardDescription>
                    </div>
                    <Badge variant="outline">{project.assigned_users?.length || 0} {t('projects.users')}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <MapPin className="w-4 h-4" />
                    <span className="truncate">{project.location}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="w-4 h-4" />
                    <span>{project.start_date} - {project.end_date}</span>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default Projects;